import { applyTheming, getLanguageCode } from "@pandas/core-ui";
import { i18n } from "i18next";
import TagManager from "react-gtm-module";
import { servicesOrchestratorValues } from "@/config/services";
import initAssessmentEngine from "@/lib/assessments/initAssessmentEngine";
import logger, { ChangeLoggerLevelFromConfig } from "@/lib/logging/logger";
import type { InitSearch } from "@/routes/$locale/$partner/-search-params";
import { configuratorApi, platformApi } from "@/services";
import {
	type ConfiguratorAppGlobalConfig,
	type ConfiguratorConfig,
	globalConfigurationInitialState,
} from "@/services/types";
import { useLocalStorageStore, useStore } from "@/store";
import { configStyle } from "@/utils/configStyle";
import { updateLocalization } from "@/utils/translations";
import { emailValidator } from "@/utils/validators";
import ActivateServiceWorker from "../../serviceWorkerActivator";
import { flowInitializer } from "./flowInitializer";
import { resetState } from "./resetState";

export const appInitializer = async (partnerPathname: string, queryParams: InitSearch, i18next: i18n) => {
	const setGlobalConfiguration = useStore.getState().setGlobalConfigurationInfo;
	const setConfigurationInfo = useStore.getState().setConfigurationInfo;
	const setPartnerInfo = useStore.getState().setPartnerInfo;
	const setUserInfo = useStore.getState().setUserInfo;
	const isAppInitializing = useStore.getState().isAppInitializing;
	const isTablet = queryParams.tablet ?? false;
	const disableCookies = queryParams.disableCookies ?? false;

	// Apply query params to store
	const setDeviceInfo = useLocalStorageStore.getState().setDeviceInfo;
	if (queryParams.email && emailValidator(queryParams.email))
		setUserInfo({ ...useStore.getState().userInfo, email: queryParams.email });
	if (queryParams.imei) setDeviceInfo({ imei: queryParams.imei });
	if (queryParams.brand) setDeviceInfo({ brand: queryParams.brand });
	if (queryParams.model) setDeviceInfo({ model: queryParams.model });
	if (queryParams.capacity) setDeviceInfo({ capacity: queryParams.capacity });
	if (queryParams.memory) setDeviceInfo({ memory: queryParams.memory });
	setDeviceInfo({ deviceType: isTablet ? "tablet" : "smartphone" });

	const session = queryParams.sessionId ? await platformApi.getSessionById(queryParams.sessionId) : null;
	const locationId = queryParams.locationId ?? session?.last_location_id ?? undefined;

	const partnerConfig = await configuratorApi.getConfig<ConfiguratorConfig & ConfiguratorAppGlobalConfig>(
		partnerPathname,
		locationId,
	);
	const { logging, sound_test, serviceWorker, objectDetectionVersion, preloadAI } = partnerConfig;
	const globalConfigurationResponse = logging
		? { logging, sound_test, serviceWorker, objectDetectionVersion, preloadAI: preloadAI ?? false }
		: globalConfigurationInitialState;

	const theming = applyTheming(partnerConfig, partnerPathname, servicesOrchestratorValues.urls.cdn, queryParams.theme);

	if (servicesOrchestratorValues.isProduction && disableCookies !== true)
		TagManager.initialize({ gtmId: "GTM-NRW4XP9F" });

	setGlobalConfiguration(globalConfigurationResponse);
	setConfigurationInfo({ ...partnerConfig, theming, partnerSettings: partnerConfig });

	const logLevel = globalConfigurationResponse.logging.uploadlevel;
	ChangeLoggerLevelFromConfig(logLevel);

	const partnerResponse = await platformApi.getPartner(partnerPathname);
	setPartnerInfo(partnerResponse);
	setUserInfo({ countryCode: partnerResponse.country ?? undefined });

	configStyle(theming, queryParams.theme);

	const { flow, functionalResults, graderResults, shouldKeepAssessmentResults } = await flowInitializer(
		partnerConfig,
		partnerConfig,
		partnerResponse,
		queryParams,
		session,
	);

	logger.injectCommonProp("pandas_partner_code", partnerPathname);
	logger.injectCustomProp("flow", flow);
	logger.injectCommonProp("user_agent", navigator.userAgent);

	await updateLocalization(i18next, getLanguageCode(sessionStorage.getItem("selectedLanguage") ?? "en"));

	await initAssessmentEngine(
		flow,
		partnerConfig.assessments,
		functionalResults,
		graderResults,
		partnerConfig.componentVersion,
	);

	if (isAppInitializing) {
		if (!shouldKeepAssessmentResults) resetState();
		await ActivateServiceWorker(
			globalConfigurationResponse.serviceWorker,
			globalConfigurationResponse.objectDetectionVersion,
		);
	}
};
